import { useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { FaviconUpdater } from "../FaviconUpdater";
import { Header } from "../Header";
import { Sidebar } from "../Sidebar";

export function ProtectedLayout() {
  const { status, activeOrganisation } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  if (status === "anonymous") {
    return <Navigate to="/login" replace />;
  }

  if (!activeOrganisation) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-white p-6 text-center dark:bg-slate-900">
        <h1 className="text-lg font-semibold">No active organisation</h1>
        <p className="max-w-md text-sm text-slate-600 dark:text-slate-400">
          Your account is not currently a member of any organisation. Ask an administrator to invite
          you, or sign up to create your own.
        </p>
      </div>
    );
  }

  // The slug in the URL is for UX. Server-side, the JWT determines the active
  // org and the API rejects mismatches. Here we just normalise the URL if a
  // user lands on /o/<wrong-slug>/...
  if (slug && slug !== activeOrganisation.slug) {
    return <Navigate to={`/o/${activeOrganisation.slug}/dashboard`} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100">
      <FaviconUpdater />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-brand-600 focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Skip to main content
      </a>
      <Header onMenuToggle={() => setMobileMenuOpen((v) => !v)} />
      <div className="flex flex-1">
        <Sidebar
          orgSlug={activeOrganisation.slug}
          isOpen={isMobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
        <main id="main" className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
